import { AxiosResponse, HttpStatusCode } from "axios";
import { t } from "i18next";

import { errors } from "../../constants";
import { ResponseStatusEnum } from "../../enums";
import {
  ApiException,
  IApiError,
  TApiResponse,
  TApiSuccess,
} from "../../models";
import { Log } from "../log.helpers";
import { EMPTY_RESPONSE_ERROR, normalizeApiError } from "./api-utils";

export const checkResponseStatus = async <T>(
  response: AxiosResponse<TApiResponse<T> | TApiSuccess<T>>,
): Promise<TApiSuccess<T>> => {
  if (!response?.status) {
    throw new ApiException(EMPTY_RESPONSE_ERROR);
  }

  if (
    response.status === HttpStatusCode.NoContent &&
    response.data?.status !== ResponseStatusEnum.Error
  ) {
    return response?.data;
  }

  if (response.data?.status === ResponseStatusEnum.Error) {
    throw new ApiException(response.data, response.status);
  }

  if (
    response.status < HttpStatusCode.Ok ||
    response.status >= HttpStatusCode.MultipleChoices
  ) {
    if (response.status !== HttpStatusCode.Conflict) {
      Log.error(
        t(errors.common.responseErrorStatus, { status: response.status }),
        response,
      );
    }

    const normalizedError = normalizeApiError({
      apiError: response.data as unknown as IApiError,
      fallbackTranslationKey: errors.common.invalidResponseStatus,
    });
    throw new ApiException(normalizedError, response.status);
  }

  if (response.data?.status === ResponseStatusEnum.Success) {
    return response.data;
  }

  if ((response.data as unknown as TApiSuccess<T>) instanceof Blob) {
    return {
      status: ResponseStatusEnum.Success,
      data: response.data,
    };
  }

  // handle osrm gateway response
  if ((response.data as any)?.code === "Ok") {
    return {
      status: ResponseStatusEnum.Success,
      data: response.data,
    };
  }

  Log.error(t(errors.common.invalidAPIResponse), response);
  throw new Error(t(errors.common.invalidResponse));
};
